import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import type { Track } from '@react-input/mask';
import {
  Box,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { AiBubble, UserBubble } from './ui/chat-bubble';
import { ThinkingCard, ThinkingElapsedRow } from './ui/thinking-card';
import { QuickReplies } from './ui/quick-replies';
import { ConsentPanel } from './ui/consent-panel';
import { SimpleChatInput } from './ui/simple-chat-input';
import { TypewriterText } from './ui/typewriter-text';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { ChatStep } from './lib/chat-flow-machine';
import { freeTextFallback } from './lib/free-text-fallback';
import { isEmailValid, validatePhone } from '@coverright/features';
import { getCId, getDisclamers, getStoredUser, setStoredUser, covertDateToSeverFormat } from '@coverright/utils';
import {
  CountiesByZipDocument,
  CountiesByZipQuery,
  CountiesByZipQueryVariables,
  PlansCountByZipDocument,
  PlansCountByZipQuery,
  PlansCountByZipQueryVariables,
  useSaveMedicareQuoteMutation,
} from '@coverright/data-access/medicare';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import { MedicareQuoteFilterExtraBenefit } from '@coverright/data-access/types/medicare';
import { Gender } from '@coverright/data-access/types/medigap';
import { ProfileSource } from '@coverright/data-access/types/enrollment';
import { useCreateProfile } from '@coverright/widgets/create-account-form/api/use-create-profile';
import { KeycloakContext } from '@coverright/shared/keycloak';
import { environment } from '../../../environments/environment';
import { ChatCard } from './ui/chat-card';

interface Turn {
  id: string;
  from: 'ai' | 'user';
  content: React.ReactNode;
}

let turnCounter = 0;
const nextId = () => `t${++turnCounter}`;

// Fake "thinking" steps shown briefly before a scripted reply — there's no
// real reasoning happening, just a couple of plausible-looking beats so the
// bot doesn't feel like it's instantly playing back canned text.
const GENERIC_THINKING_STEPS = [
  'Reading your answer...',
  'Checking Medicare details...',
  'Comparing your options...',
  'Reviewing the results...',
  'Double-checking that...',
  'Putting it together...',
];

const pickThinkingSteps = (count = 2) => {
  const pool = [...GENERIC_THINKING_STEPS];
  const picked: string[] = [];
  for (let i = 0; i < count && pool.length; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    picked.push(pool.splice(idx, 1)[0]);
  }
  return picked;
};

const STEP_PLACEHOLDERS: Partial<Record<ChatStep, string>> = {
  greeting: 'Type your name...',
  zip: 'Type your ZIP code...',
  medigapTeaser: 'Yes or no...',
  medigapDob: 'MM/DD/YYYY...',
  medigapGender: 'Male or female...',
  medigapTobacco: 'Yes or no...',
  trust: 'Type your answer...',
  registrationFirstName: 'Your first name...',
  registrationEmail: 'you@example.com...',
  registrationPhone: '(000) 000-0000...',
};

const getPlaceholder = (step: ChatStep) => STEP_PLACEHOLDERS[step] ?? 'Or type a message...';

// Auto-inserts the "1" country code when the user starts typing a US number
// without it — copied from libs/ui/inputs/src/lib/phone-number-input.tsx.
const phoneTrack: Track = ({ inputType, value, data, selectionStart, selectionEnd }) => {
  if (inputType === 'insert' && !/^\D*1/.test(data) && selectionStart <= 1) {
    return `1${data}`;
  }
  if (inputType !== 'insert' && selectionStart <= 1 && selectionEnd < value.length) {
    if (selectionEnd > 2) {
      return '1';
    }
    if (selectionEnd === 2) {
      return false;
    }
  }
  return data;
};

const STEP_MASKS: Partial<Record<ChatStep, { mask: string; replacement: Record<string, RegExp>; track?: Track }>> = {
  zip: { mask: '_____', replacement: { _: /\d/ } },
  medigapDob: { mask: 'mm/dd/yyyy', replacement: { d: /\d/, m: /\d/, y: /\d/ } },
  registrationPhone: { mask: '+_ (___) ___-____', replacement: { _: /\d/ }, track: phoneTrack },
};

const getMask = (step: ChatStep) => STEP_MASKS[step];

export function ChatPage() {
  // The opening line renders straight into `turns` (skipping the queue) so it
  // appears immediately on load — there's nothing to "think about" yet.
  const [turns, setTurns] = useState<Turn[]>([
    {
      id: nextId(),
      from: 'ai',
      content: "Hello! I'm the CoverRight AI assistant, and I'll help you find the right Medicare plan. What should I call you?",
    },
  ]);
  const [queue, setQueue] = useState<Turn[]>([]);
  const [animating, setAnimating] = useState(true);
  const [step, setStep] = useState<ChatStep>('greeting');
  const [thinking, setThinking] = useState<{ id: string; steps: string[]; stepDurationMs?: number; onDone: () => void } | null>(null);
  const [busy, setBusy] = useState(false);
  const [registering, setRegistering] = useState(false);

  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [tobacco, setTobacco] = useState<'true' | 'false' | ''>('');
  const [planCounts, setPlanCounts] = useState<{ maPlansCount: string; maOrgCount: string; total: number } | null>(null);

  const [saveQuote] = useSaveMedicareQuoteMutation();
  const createProfile = useCreateProfile();
  const { init, getHash } = React.useContext(KeycloakContext);

  const transcriptEndRef = useRef<HTMLDivElement>(null);

  const addAi = (content: React.ReactNode) => {
    const id = nextId();
    setQueue((q) => [...q, { id, from: 'ai', content }]);
  };
  const addUser = (content: React.ReactNode) => {
    const id = nextId();
    setQueue((q) => [...q, { id, from: 'user', content }]);
  };

  const thinkSteps = (steps: string[], onDone: () => void, stepDurationMs?: number) =>
    setThinking({
      id: nextId(),
      steps,
      stepDurationMs,
      onDone: () => {
        setThinking(null);
        onDone();
      },
    });

  const think = (label: string, onDone: () => void, durationMs?: number) =>
    thinkSteps([label], onDone, durationMs);

  // Reveal queued turns one at a time — the next turn only appears once the
  // previous AI message has finished typing (see TypewriterText onComplete).
  // Only the first AI message *responding to the user* gets a fake "thinking"
  // beat first — further AI messages in the same burst (no user turn between
  // them) just keep typing straight through, so paired bubbles don't get an
  // extra loading blip between them.
  React.useEffect(() => {
    if (thinking || animating || queue.length === 0) return;
    const [next] = queue;
    const reveal = () => {
      // Slice the *current* queue at reveal time, not the snapshot captured
      // when this effect ran — messages can be appended to it in between
      // (e.g. while an awaited network call was still in flight).
      setQueue((q) => (q[0]?.id === next.id ? q.slice(1) : q));
      setTurns((t) => (t.some((x) => x.id === next.id) ? t : [...t, next]));
      if (next.from === 'ai' && typeof next.content === 'string') {
        setAnimating(true);
      }
    };
    const lastRevealed = turns[turns.length - 1];
    const continuesAiBurst = next.from === 'ai' && lastRevealed?.from === 'ai';
    if (next.from === 'ai' && !continuesAiBurst) {
      thinkSteps(pickThinkingSteps(), reveal, 380);
    } else {
      reveal();
    }
  }, [thinking, animating, queue]);

  const [freeText, setFreeText] = useState('');
  const freeTextInputRef = useRef<HTMLTextAreaElement>(null);
  const handleFreeText = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = freeText.trim();
    if (!trimmed) return;
    setFreeText('');
    if (step === 'greeting') {
      handleGreetingContinue(trimmed);
      return;
    }
    if (step === 'zip') {
      handleZipInput(trimmed);
      return;
    }
    if (step === 'medigapDob') {
      handleDobInput(trimmed);
      return;
    }
    if (step === 'registrationFirstName') {
      handleFirstNameInput(trimmed);
      return;
    }
    if (step === 'registrationEmail') {
      handleEmailInput(trimmed);
      return;
    }
    if (step === 'registrationPhone') {
      handlePhoneInput(trimmed);
      return;
    }
    addUser(trimmed);
    addAi(freeTextFallback(step));
  };

  // Throttled so the per-character typewriter tick (called dozens of times a
  // second) doesn't re-trigger a scroll on every single tick — that's what
  // was making the scroll feel jerky. Turn/step changes are infrequent, so
  // those always scroll (smoothly); the typing ticks just nudge it along.
  const lastScrollAtRef = useRef(0);
  const scrollToBottom = (smooth: boolean) => {
    const now = Date.now();
    if (!smooth && now - lastScrollAtRef.current < 200) return;
    lastScrollAtRef.current = now;
    transcriptEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
  };

  React.useEffect(() => {
    scrollToBottom(true);
  }, [turns, thinking, step, animating, registering]);

  React.useEffect(() => {
    if (!thinking && !animating && queue.length === 0) {
      freeTextInputRef.current?.focus();
    }
  }, [step, thinking, animating, queue.length]);

  const fetchPlanCount = async (zip: string) => {
    const { data } = await mainBackendApolloClient.query<PlansCountByZipQuery, PlansCountByZipQueryVariables>({
      query: PlansCountByZipDocument,
      variables: { zip },
      fetchPolicy: 'network-only',
    });
    const maPlansCount = data.plansCountByZip.maPlansCount;
    const pdpPlansCount = data.plansCountByZip.pdpPlansCount;
    const maOrgCount = data.plansCountByZip.maPlansParentOrgCount;
    const total = (parseFloat(maPlansCount) || 0) + (parseFloat(pdpPlansCount) || 0);
    setPlanCounts({ maPlansCount, maOrgCount, total });
    return total;
  };

  // ── Step 0: greeting ──────────────────────────────────────────────────────

  const handleGreetingContinue = (value: string | null) => {
    const trimmed = value?.trim();
    if (trimmed) {
      addUser(trimmed);
      setStoredUser({ ...getStoredUser(), firstName: trimmed });
      addAi(`Nice to meet you, ${trimmed}! Let's find you some Medicare plans.`);
    } else {
      addUser('Skip');
      addAi("No problem — let's find you some Medicare plans. I'll ask for your name again when we set up your account.");
    }
    addAi("Medicare plan availability varies even between neighboring ZIP codes, so to show you the right options I'll need your ZIP code.");
    setStep('zip');
  };

  // ── Step 1: zip ───────────────────────────────────────────────────────────

  const [zipCandidates, setZipCandidates] = useState<{ countyname: string; state: string }[] | null>(null);
  const [pendingZip, setPendingZip] = useState('');

  const completeZip = async (zip: string, county: string, state: string) => {
    setStoredUser({ ...getStoredUser(), zip, countyName: county, state });
    setBusy(true);
    try {
      const res = await saveQuote({
        variables: {
          data: {
            cId: getCId(),
            zip,
            county,
            filters: { extraBenefits: [MedicareQuoteFilterExtraBenefit.DrugCoverage] },
          },
        },
      });
      setStoredUser({ ...getStoredUser(), quoteId: res.data?.saveMedicareQuote.maQuoteID });
    } finally {
      setBusy(false);
    }
    think('Looking up your area...', async () => {
      addAi(`Found it — ZIP ${zip}, ${county} County, ${state}.`);
      const total = await fetchPlanCount(zip);
      addAi(`There are ${total} Medicare plans available in your area.`);
      addAi(
        "Besides Medicare Advantage, there's also Original Medicare paired with a Medigap plan and a separate Part D prescription drug plan — some people find it more cost-effective long-term. Want me to include that option?"
      );
      setStep('medigapTeaser');
    });
  };

  const handleZipInput = async (raw: string) => {
    const zip = raw.replace(/\D/g, '').slice(0, 5);
    if (zip.length !== 5) {
      addUser(raw);
      addAi("That doesn't look like a valid ZIP code — mind sending it again?");
      return;
    }
    addUser(zip);
    setBusy(true);
    const { data } = await mainBackendApolloClient.query<CountiesByZipQuery, CountiesByZipQueryVariables>({
      query: CountiesByZipDocument,
      variables: { zip },
    });
    setBusy(false);
    const counties = data.plans_zip_county_fips;
    if (counties.length === 1) {
      await completeZip(zip, counties[0].countyname, counties[0].state);
    } else if (counties.length > 1) {
      setZipCandidates(counties);
      setPendingZip(zip);
      addAi('Your ZIP code covers a few different counties — which one do you live in?');
      setStep('zipCounty');
    } else {
      addAi("I couldn't find that ZIP code — mind double-checking and sending it again?");
    }
  };

  const handleZipCountySelect = (countyname: string) => {
    const match = zipCandidates?.find((c) => c.countyname === countyname);
    addUser(countyname);
    if (match) {
      completeZip(pendingZip, match.countyname, match.state);
    }
  };

  // ── Step 2: medigap teaser ────────────────────────────────────────────────

  const handleMedigapTeaser = (value: string) => {
    addUser(value === 'yes' ? 'Yes, show me that too' : 'No, just Medicare Advantage');
    if (value === 'yes') {
      addAi(
        "Great — showing Medigap and Part D plans takes a bit more information, since pricing depends on your date of birth, gender, and tobacco use. Let's start with your date of birth."
      );
      setStep('medigapDob');
    } else {
      goToTrust();
    }
  };

  const handleDobInput = (raw: string) => {
    const value = raw.trim();
    const valid = moment(value, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY'], true).isValid() || moment(value).isValid();
    if (!valid) {
      addUser(raw);
      addAi("I couldn't quite read that date — mind sending it like MM/DD/YYYY?");
      return;
    }
    addUser(value);
    setDob(value);
    addAi("Thanks. What's your gender?");
    setStep('medigapGender');
  };

  const handleGenderSelect = (value: Gender) => {
    addUser(value === Gender.M ? 'Male' : 'Female');
    setGender(value);
    addAi('Last one — do you use tobacco?');
    setStep('medigapTobacco');
  };

  const handleTobaccoSelect = (value: 'true' | 'false') => {
    addUser(value === 'true' ? 'Yes, I use tobacco' : 'No tobacco use');
    setTobacco(value);
    setStoredUser({
      ...getStoredUser(),
      birthDate: covertDateToSeverFormat(dob),
      gender,
      tobacco: value,
    });
    think('Updating your plan options...', () => {
      addAi("Got it — I've saved that. You'll be able to see Medigap and Part D options alongside your Medicare Advantage plans.");
      goToTrust();
    });
  };

  // ── Step 3: trust ─────────────────────────────────────────────────────────

  const goToTrust = () => {
    addAi(
      'Everything you share with me is securely protected and used only to find plans for you — we never sell or share your personal information with third parties. Want to see the full Medicare disclaimers?'
    );
    setStep('trust');
  };

  const handleSkipMedigapDetails = () => {
    addUser('Just show me Medicare Advantage');
    goToTrust();
  };

  const handleTrust = (value: string) => {
    if (value === 'show') {
      addUser('Show me the disclaimers');
      addAi(
        <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
          <Typography variant={'caption'} color={'text.secondary'}>
            {getDisclamers('PERSONAL_INFO_ONBOARDING', { maOrgCount: planCounts?.maOrgCount, maPlansCount: planCounts?.maPlansCount } as any)}
          </Typography>
        </Box>
      );
    } else {
      addUser('No thanks, keep going');
    }
    startRegistration();
  };

  // ── Step 4: registration ─────────────────────────────────────────────────

  const startRegistration = () => {
    if (getStoredUser()?.firstName) {
      addAi("Let's get your account set up so I can save your recommendations — what's your email address?");
      setStep('registrationEmail');
    } else {
      addAi("Let's get your account set up so I can save your recommendations — what's your first name?");
      setStep('registrationFirstName');
    }
  };

  const handleFirstNameInput = (raw: string) => {
    const value = raw.trim();
    addUser(value);
    setStoredUser({ ...getStoredUser(), firstName: value });
    addAi("What's your email address?");
    setStep('registrationEmail');
  };

  const handleEmailInput = (raw: string) => {
    const value = raw.trim();
    if (!isEmailValid(value)) {
      addUser(raw);
      addAi("That doesn't look like a valid email — mind sending it again?");
      return;
    }
    addUser(value);
    setStoredUser({ ...getStoredUser(), email: value });
    addAi("Last step — what's your phone number? I'll use it to confirm this is really you and keep your account secure.");
    addAi(<ConsentPanel open maOrgCount={planCounts?.maOrgCount} maPlansCount={planCounts?.maPlansCount} />);
    setStep('registrationPhone');
  };

  const redirectTo = (path: string) => {
    window.location.href = environment.consumerAiBaseUrl + path;
  };

  // Shows a loading state *immediately* on submit, then runs the real
  // network calls while it's up — rather than validating silently first and
  // only showing feedback once something has already resolved, which read as
  // the app hanging. stepDurationMs: Infinity keeps ThinkingCard "sticky" —
  // we advance/clear it ourselves as each real step actually completes.
  const handlePhoneInput = async (raw: string) => {
    addUser(raw);
    const digits = raw.replace(/\D/g, '');
    const normalized = digits.length === 10 ? '+1' + digits : digits.length === 11 && digits.startsWith('1') ? '+' + digits : '';

    setRegistering(true);
    setThinking({ id: nextId(), steps: ['Verifying your number...'], stepDurationMs: Infinity, onDone: () => {} });

    if (!normalized || !(await validatePhone(normalized))) {
      setThinking(null);
      setRegistering(false);
      addAi("That doesn't look like a valid phone number — mind trying again?");
      return;
    }

    setStoredUser({ ...getStoredUser(), phoneNumber: normalized });
    setThinking({ id: nextId(), steps: ['Creating your account...'], stepDurationMs: Infinity, onDone: () => {} });

    try {
      const userProfile = await createProfile({
        email: getStoredUser()?.email,
        firstName: getStoredUser()?.firstName,
        phoneNumber: normalized,
        source: ProfileSource.ApplicationOnboarding,
      });
      if (userProfile?.userToken) {
        await init(userProfile.userToken as any);
      }
      setThinking({ id: nextId(), steps: ['Redirecting to your personal platform...'], stepDurationMs: Infinity, onDone: () => {} });
      redirectTo('/analyzing-info' + getHash());
    } catch (err) {
      console.error(err);
      setThinking(null);
      setRegistering(false);
      addAi("Something went wrong creating your account — mind trying that again?");
    }
  };

  // ── Composer (current step's interactive control) ───────────────────────

  const renderComposer = () => {
    if (thinking || animating || registering || queue.length > 0) return null;

    switch (step) {
      case 'greeting':
        return null;

      case 'zip':
        return null;

      case 'zipCounty':
        return (
          <QuickReplies
            options={(zipCandidates ?? []).map((c) => ({ label: `${c.countyname}, ${c.state}`, value: c.countyname }))}
            onSelect={handleZipCountySelect}
          />
        );

      case 'medigapTeaser':
        return <QuickReplies options={[{ label: 'Yes, show me that too', value: 'yes' }, { label: 'No, just Medicare Advantage', value: 'no' }]} onSelect={handleMedigapTeaser} />;

      case 'medigapDob':
        return (
          <QuickReplies
            options={[{ label: 'Just show me Medicare Advantage', value: 'skip' }]}
            onSelect={handleSkipMedigapDetails}
          />
        );

      case 'medigapGender':
        return (
          <QuickReplies
            options={[{ label: 'Male', value: Gender.M }, { label: 'Female', value: Gender.F }]}
            onSelect={(v) => handleGenderSelect(v as Gender)}
          />
        );

      case 'medigapTobacco':
        return (
          <QuickReplies
            options={[{ label: 'No tobacco use', value: 'false' }, { label: 'Uses tobacco', value: 'true' }]}
            onSelect={(v) => handleTobaccoSelect(v as 'true' | 'false')}
          />
        );

      case 'trust':
        return <QuickReplies options={[{ label: 'Show me the disclaimers', value: 'show' }, { label: 'No thanks, keep going', value: 'skip' }]} onSelect={handleTrust} />;

      case 'registrationFirstName':
      case 'registrationEmail':
      case 'registrationPhone':
        return null;

      default:
        return null;
    }
  };

  return (
    <Container maxWidth={'sm'} sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: { xs: 3, sm: 6 }, pb: 4 }}>

      <ChatCard>
        <OverlayScrollbarsComponent options={{ scrollbars: { autoHide: 'move' } }} style={{ flex: 1 }} defer>
          <Stack id={'chat-message-area'} gap={1.25} sx={{ p: 2, background: 'rgba(249,250,251,0.5)' }}>
            {turns.map((turn, i) => {
              const isLastAi = turn.from === 'ai' && i === turns.length - 1 && !animating && !thinking && queue.length === 0;
              return turn.from === 'ai' ? (
                <AiBubble
                  key={turn.id}
                  showActions={isLastAi}
                  text={typeof turn.content === 'string' ? turn.content : undefined}
                >
                  {typeof turn.content === 'string' ? (
                    <TypewriterText
                      text={turn.content}
                      onTick={() => scrollToBottom(false)}
                      onComplete={() => setAnimating(false)}
                    />
                  ) : (
                    turn.content
                  )}
                </AiBubble>
              ) : (
                <UserBubble key={turn.id}>{turn.content}</UserBubble>
              );
            })}
            {thinking && <ThinkingCard key={thinking.id} steps={thinking.steps} stepDurationMs={thinking.stepDurationMs} onDone={thinking.onDone} />}
            {renderComposer()}
            <div ref={transcriptEndRef} />
          </Stack>
        </OverlayScrollbarsComponent>

        {thinking && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f3f4f6', background: 'rgba(249,250,251,0.5)' }}>
            <ThinkingElapsedRow />
          </Box>
        )}
      </ChatCard>


      <Box sx={{ position: 'sticky', bottom: 0, bgcolor: '#f8fafc', pt: 1.5 }}>
        <SimpleChatInput
          input={freeText}
          setInput={setFreeText}
          handleSubmit={handleFreeText}
          isTyping={!!thinking || animating || registering}
          placeholder={getPlaceholder(step)}
          inputRef={freeTextInputRef}
          mask={getMask(step)?.mask}
          maskReplacement={getMask(step)?.replacement}
          maskTrack={getMask(step)?.track}
        />
      </Box>
    </Container>
  );
}
